import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Chip,
  Stack,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  Link,
} from '@mui/material';
import type { SessionSummaryDto } from '@replayops/contracts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import BugReportIcon from '@mui/icons-material/BugReport';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ShareIcon from '@mui/icons-material/Share';
import LaunchIcon from '@mui/icons-material/Launch';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  ReplayEngine,
  NativeReplayEngine,
  isNativeRecording,
  extractInspectors,
  EventType,
  type PlayerState,
  type PlaybackSpeed,
  type ReplayTimeline,
  type TimelineMarker,
  buildHighlights,
  type Highlight,
  type InspectorData,
  type PerfSample,
  compileSteps,
  type RerunStep,
} from '@replayops/replay-core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMe, useSessionEvents, useSession } from '../api/hooks.js';
import { api } from '../api/client.js';

/** Glyph + colour per highlight kind. */
const HIGHLIGHT_GLYPH: Record<string, string> = { rage: '⚡', error: '🐛', idle: '⏸', exit: '🚪' };
const HIGHLIGHT_COLOR: Record<string, string> = {
  rage: '#e11d48',
  error: '#f5a623',
  idle: '#6366f1',
  exit: '#64748b',
};
import { safeHttpUrl } from '../lib/safeUrl.js';
import { DomInspector } from '../components/DomInspector.js';
import { CreateIssueDialog } from '../components/CreateIssueDialog.js';
import { TesterModeDialog, type TesterContext } from '../components/TesterModeDialog.js';
import html2canvas from 'html2canvas';
import { ShareDialog } from '../components/ShareDialog.js';
import { RerunDialog } from '../components/RerunDialog.js';
import { ReplayScrubber } from '../components/ReplayScrubber.js';
import { Markdown } from '../components/Markdown.js';
import { useT } from '../i18n/useT.js';

interface ApmLink {
  kind: 'trace' | 'search' | 'analytics' | 'replay';
  label: string;
  url: string;
  traceId?: string;
}
interface ApmSessionLinks {
  provider: string;
  links: ApmLink[];
}
/** What the backend ran for one request the page made, as CoverOps reports it. */
interface BackendMethod {
  className: string;
  method: string;
  filePath: string | null;
  lines: number[];
  depth: number;
}
interface BackendRequest {
  traceId: string;
  label: string;
  service: string;
  startedAtMs: number;
  durationMs: number;
  methods: BackendMethod[];
}
interface BackendSessionResult {
  provider: string;
  /** Connected, but the package no longer includes it; nothing was asked. */
  notIncluded?: boolean;
  /** Null when the integration is connected but recorded nothing for this session. */
  session: {
    sessionId: string;
    services: string[];
    requestCount: number;
    methodCount: number;
    requests: BackendRequest[];
  } | null;
}
interface StackFrame {
  raw: string;
  function?: string;
  url?: string;
  line?: number;
  resolved?: { source: string; line?: number; column?: number; name?: string };
}

const STAGE_W = 1440;
const STAGE_H = 900;
/** Cap the replay stage height so the player shares the screen with the
 * DevTools panel docked below it (and a tall mobile recording stays visible). */
const MAX_STAGE_H = 480;

const MARKER_COLOR: Record<string, string> = {
  click: '#2ec2b3',
  rageClick: '#e11d48',
  error: '#ef4444',
  navigation: '#22c55e',
  custom: '#f59e0b',
};

function fmt(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const secs = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

const LEVEL_COLOR: Record<string, string> = {
  error: '#ef4444',
  warn: '#f59e0b',
  info: '#3b82f6',
  log: '#6b7280',
  debug: '#94a3b8',
  navigation: '#22c55e',
};

/** Network panel type filters, DevTools-style. */
const NET_TYPES: { key: string }[] = [
  { key: 'all' },
  { key: 'xhr' },
  { key: 'js' },
  { key: 'css' },
  { key: 'img' },
  { key: 'font' },
  { key: 'media' },
  { key: 'other' },
];

/** Console panel level filters. `navigation` rows come from the timeline. */
const LOG_LEVELS: { key: string }[] = [
  { key: 'all' },
  { key: 'log' },
  { key: 'info' },
  { key: 'warn' },
  { key: 'error' },
  { key: 'debug' },
  { key: 'navigation' },
];

/** Rows rendered per inspector tab before "Show all" — thousands of DOM rows
 * make playback stutter, and nobody scrubs a list that long. */
const ROW_CAP = 300;

/** Width of the waterfall column at the right edge of each network row. */
const WF_W = 150;
const WF_PAD = 12; // row's right padding (px: 1.5)
const PLAYHEAD = '#6366f1';

const VITAL_COLOR: Record<string, string> = {
  good: '#16a34a',
  'needs-improvement': '#f59e0b',
  poor: '#ef4444',
};

// Brand teal (matches replayops.ai / the app theme primary).
const TEAL = '#2ec2b3';

function statusColor(status: number): string {
  if (status === 0) return '#ef4444'; // network error
  if (status < 300) return TEAL; // success → brand accent
  if (status < 400) return '#0ea5e9';
  if (status < 500) return '#f59e0b';
  return '#ef4444';
}

// HTTP method accent — muted, brand-led (GET uses the teal accent). Used as the
// text color on a soft neutral chip so it never competes with the status badge.
const METHOD_COLOR: Record<string, string> = {
  GET: '#0f8f83',
  POST: '#0369a1',
  PUT: '#b45309',
  PATCH: '#b45309',
  DELETE: '#b91c1c',
};
const methodColor = (m: string): string => METHOD_COLOR[(m || '').toUpperCase()] ?? '#475569';

function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname.length > 1 ? u.host + u.pathname : u.host;
  } catch {
    return url.slice(0, 60);
  }
}

/** Tab label with an optional count badge. */
function tabLabel(name: string, count?: number): string {
  return count ? `${name} (${count})` : name;
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <Typography sx={{ p: 2 }} color="text.secondary">
      {children}
    </Typography>
  );
}

/** Pre-filled issue title — leads with the first JS error if the session had one. */
function issueTitle(sessionId: string, inspectors: InspectorData | null): string {
  const firstError = inspectors?.errors[0]?.message;
  if (firstError) return `Session error: ${firstError.slice(0, 120)}`;
  return `Investigate session ${sessionId.slice(0, 8)}`;
}

/** Pre-filled issue body summarising what the replay observed. */
function issueBody(
  labels: { reportedFrom: string },
  sessionId: string,
  timeline: ReplayTimeline | null,
  inspectors: InspectorData | null,
): string {
  const lines = [labels.reportedFrom, '', `**Session:** \`${sessionId}\``];
  if (timeline) lines.push(`**Duration:** ${fmt(timeline.durationMs)}`);

  const errors = inspectors?.errors ?? [];
  if (errors.length) {
    lines.push('', `**Errors (${errors.length}):**`);
    for (const e of errors.slice(0, 5)) {
      lines.push(`- \`${secs(e.offsetMs)}\` ${e.message}${e.source ? ` (${e.source}:${e.line ?? '?'})` : ''}`);
    }
  }

  const failedReqs = (inspectors?.network ?? []).filter((n) => !n.ok);
  if (failedReqs.length) {
    lines.push('', `**Failed requests (${failedReqs.length}):**`);
    for (const n of failedReqs.slice(0, 5)) {
      lines.push(`- \`${n.status || 'ERR'}\` ${n.method} ${shortUrl(n.url)}`);
    }
  }

  return lines.join('\n');
}

/** 2-letter ISO country code → flag emoji (regional indicator symbols). */
function countryFlag(cc?: string): string {
  if (!cc || cc.length !== 2 || !/^[a-zA-Z]{2}$/.test(cc)) return '';
  const base = 0x1f1e6;
  const up = cc.toUpperCase();
  return String.fromCodePoint(base + up.charCodeAt(0) - 65, base + up.charCodeAt(1) - 65);
}

function refHost(url: string | undefined, directLabel: string): string {
  if (!url) return directLabel;
  try {
    return new URL(url).hostname || url;
  } catch {
    return url;
  }
}

function DetailField({ label, value, mono }: { label: string; value?: ReactNode; mono?: boolean }) {
  const empty = value === undefined || value === null || value === '';
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        noWrap
        title={typeof value === 'string' ? value : undefined}
        sx={{ fontWeight: 600, fontFamily: mono ? 'monospace' : undefined, color: empty ? 'text.disabled' : 'text.primary' }}
      >
        {empty ? '—' : value}
      </Typography>
    </Box>
  );
}

/** Visitor + environment metadata for the recording, shown above the player. */
function VisitorDetails({ session: s, actionCount }: { session?: SessionSummaryDto; actionCount?: number }) {
  const t = useT().replay.page;
  if (!s) return null;
  const who = s.userEmail || s.userId || `anon ${s.anonymousId.slice(0, 8)}`;
  const browser = [s.browser, s.browserVersion].filter(Boolean).join(' ');
  const os = [s.os, s.osVersion].filter(Boolean).join(' ');
  const loc = [s.city, s.region, s.country].filter(Boolean).join(', ');
  const frust = s.frustrationScore;
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
          gap: 2,
        }}
      >
        <DetailField label={t.details.visitor} value={who} />
        <DetailField label={t.details.browser} value={browser} />
        <DetailField label={t.details.os} value={os} />
        <DetailField label={t.details.device} value={s.deviceType} />
        <DetailField label={t.details.location} value={loc ? `${countryFlag(s.country)} ${loc}`.trim() : undefined} />
        <DetailField label={t.details.ip} value={s.ip} mono />
        <Tooltip title={t.details.actionsHint}>
          <Box sx={{ minWidth: 0 }}>
            <DetailField label={t.details.actions} value={actionCount?.toLocaleString()} />
          </Box>
        </Tooltip>
        <DetailField label={t.details.duration} value={fmt(s.durationMs)} />
        <DetailField label={t.details.referrer} value={refHost(s.referrer, t.direct)} />
        <DetailField label={t.details.release} value={s.releaseVersion} />
        <DetailField label={t.details.environment} value={s.environment} />
        <Tooltip title={s.entryUrl}>
          <Box sx={{ minWidth: 0 }}>
            <DetailField label={t.details.entryUrl} value={shortUrl(s.entryUrl)} />
          </Box>
        </Tooltip>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
        {typeof frust === 'number' && (
          <Chip
            size="small"
            label={`Frustration ${frust}`}
            color={frust >= 50 ? 'error' : frust >= 20 ? 'warning' : 'default'}
          />
        )}
        {!!s.errorCount && <Chip size="small" color="error" variant="outlined" label={`${s.errorCount} error${s.errorCount > 1 ? 's' : ''}`} />}
        {!!s.rageClickCount && <Chip size="small" color="warning" variant="outlined" label={`${s.rageClickCount} rage click${s.rageClickCount > 1 ? 's' : ''}`} />}
        {!!s.deadClickCount && <Chip size="small" variant="outlined" label={`${s.deadClickCount} dead click${s.deadClickCount > 1 ? 's' : ''}`} />}
        {s.isBounce && <Chip size="small" variant="outlined" label="Bounce" />}
      </Stack>
    </Paper>
  );
}

export function ReplayPage() {
  const t = useT().replay.page;
  const { projectId, sessionId } = useParams();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  // Only tester-flagged members may open tickets / use tester mode.
  const canTest = !!me?.organizations[0]?.tester;
  const { data, isLoading } = useSessionEvents(orgId, projectId, sessionId);
  const { data: session } = useSession(orgId, projectId, sessionId);

  const stageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Either engine drives the same controls — a native recording replays frames,
  // a web one replays the DOM.
  const engineRef = useRef<ReplayEngine | NativeReplayEngine | null>(null);
  const [tab, setTab] = useState(0);
  const [netOpen, setNetOpen] = useState<number | null>(null); // expanded network row
  const [netType, setNetType] = useState('all'); // network type filter
  const [netQuery, setNetQuery] = useState(''); // network URL text filter
  const [logLevel, setLogLevel] = useState('all'); // console level filter
  // Long sessions produce thousands of rows; render a page's worth unless asked.
  const [netShowAll, setNetShowAll] = useState(false);
  const [logShowAll, setLogShowAll] = useState(false);
  const [resolvedStacks, setResolvedStacks] = useState<Record<number, StackFrame[]>>({});
  const [resolving, setResolving] = useState<number | null>(null);
  const [timeline, setTimeline] = useState<ReplayTimeline | null>(null);
  // The moments worth jumping to, distilled from the markers — six good ones
  // beat a scrubber full of dots.
  const highlights: Highlight[] = useMemo(
    () => (timeline ? buildHighlights(timeline.markers, timeline.durationMs) : []),
    [timeline],
  );


  const [inspectors, setInspectors] = useState<InspectorData | null>(null);

  // Network tab: entries surviving the type + text filters, and per-type counts
  // for the chips. Entries already arrive sorted by request start.
  const netEntries = useMemo(() => {
    const all = inspectors?.network ?? [];
    const q = netQuery.trim().toLowerCase();
    return all.filter(
      (n) =>
        (netType === 'all' || n.resourceType === netType) &&
        (!q || n.url.toLowerCase().includes(q)),
    );
  }, [inspectors, netType, netQuery]);
  const netCounts = useMemo(() => {
    const counts: Record<string, number> = { all: inspectors?.network.length ?? 0 };
    for (const n of inspectors?.network ?? []) counts[n.resourceType] = (counts[n.resourceType] ?? 0) + 1;
    return counts;
  }, [inspectors]);

  // Console tab: the log stream with navigations interleaved ("Navigated to …"),
  // the way DevTools shows page moves between log lines.
  const logEntries = useMemo(() => {
    const cons = inspectors?.console ?? [];
    const navs = (timeline?.markers ?? [])
      .filter((m) => m.type === 'navigation')
      .map((m) => ({ offsetMs: m.offsetMs, level: 'navigation', message: `Navigated to ${m.label}` }));
    return [...cons, ...navs].sort((a, b) => a.offsetMs - b.offsetMs);
  }, [inspectors, timeline]);
  const logCounts = useMemo(() => {
    const counts: Record<string, number> = { all: logEntries.length };
    for (const c of logEntries) counts[c.level] = (counts[c.level] ?? 0) + 1;
    return counts;
  }, [logEntries]);
  const shownLogs = useMemo(
    () => (logLevel === 'all' ? logEntries : logEntries.filter((c) => c.level === logLevel)),
    [logEntries, logLevel],
  );

  // A web recording with events but no DOM keyframe cannot render a video —
  // surface why instead of a silent white stage.
  const missingSnapshot = useMemo(
    () =>
      !!data?.events?.length &&
      !isNativeRecording(data.events) &&
      !data.events.some((e: { type: number }) => e.type === EventType.FullSnapshot),
    [data],
  );

  // The session compiled into an executable scenario (Steps tab + Re-run).
  const steps: RerunStep[] = useMemo(
    () => (data?.events && !isNativeRecording(data.events) ? compileSteps(data.events) : []),
    [data],
  );

  // Waterfall time axis: the whole session, so bars and the playhead share one scale.
  const wfTotal = useMemo(() => {
    const maxNet = Math.max(0, ...(inspectors?.network ?? []).map((n) => n.offsetMs));
    return Math.max(timeline?.durationMs ?? 0, maxNet, 1);
  }, [inspectors, timeline]);
  const [state, setState] = useState<PlayerState>({
    playing: false,
    currentTimeMs: 0,
    durationMs: 0,
    speed: 1,
  });
  const [scale, setScale] = useState(1);
  // The recorded viewport (from the first Meta event) — so a mobile recording
  // renders narrow/tall and a desktop one wide, each fit to the panel.
  const [stageDims, setStageDims] = useState({ w: STAGE_W, h: STAGE_H });
  const [issueOpen, setIssueOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  // Steps tab: ticked-off steps while following the scenario by hand.
  const [doneSteps, setDoneSteps] = useState<Set<number>>(new Set());
  const [stepsCopied, setStepsCopied] = useState(false);
  const [testerOpen, setTesterOpen] = useState(false);
  const [testerBg, setTesterBg] = useState<string | null>(null);
  const [testerErr, setTesterErr] = useState<string | null>(null);

  // ── Tester mode (marker.io-style annotate → ticket with screenshot) ──
  const openTesterMode = async () => {
    setTesterErr(null);
    setTesterBg(null);
    setTesterOpen(true);
    try {
      // Capture the rrweb replay iframe's own document (the reconstructed page),
      // not the outer wrapper — the wrapper renders empty in html2canvas. A
      // native replay has no iframe: its canvas is already the picture, so the
      // container itself is the right capture target.
      const current = engineRef.current;
      const iframe = current instanceof ReplayEngine ? current.getIframe() : null;
      const doc = iframe?.contentDocument;
      const target = doc?.body ?? containerRef.current;
      if (!target) throw new Error('nothing to capture');
      const w = doc?.documentElement?.scrollWidth || undefined;
      const h = doc?.documentElement?.scrollHeight || undefined;
      const canvas = await html2canvas(target, {
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        scale: 1,
        width: w,
        height: h,
        windowWidth: w,
        windowHeight: h,
      });
      setTesterBg(canvas.toDataURL('image/jpeg', 0.85));
    } catch {
      // Capture can fail on cross-origin frames — fall back to annotate-only.
      const c = document.createElement('canvas');
      c.width = 1280;
      c.height = 720;
      const ctx = c.getContext('2d')!;
      ctx.fillStyle = '#f1f5f9';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.fillStyle = '#64748b';
      ctx.font = '20px sans-serif';
      ctx.fillText(t.screenshotUnavailable, 40, 60);
      setTesterBg(c.toDataURL('image/jpeg', 0.85));
    }
  };

  const testerTicket = useMutation({
    mutationFn: async (input: { title: string; description: string; screenshot: string }) =>
      (
        await api.post(`/v1/orgs/${orgId}/tickets`, {
          title: input.title,
          description: input.description || undefined,
          priority: 'HIGH',
          projectId,
          sessionId,
          screenshotUrl: input.screenshot,
        })
      ).data as { id: string },
    onSuccess: (t) => navigate(`/tickets/${t.id}`),
    onError: () => setTesterErr(t.ticketFailed),
  });

  const testerContext: TesterContext = {
    url: session?.lastUrl,
    browser: [session?.browser, session?.browserVersion].filter(Boolean).join(' ') || undefined,
    os: [session?.os, session?.osVersion].filter(Boolean).join(' ') || undefined,
    jsErrors: inspectors?.errors.length,
  };

  // APM deep-links for this session (New Relic / Datadog / AppDynamics / OTel).
  const apm = useQuery({
    enabled: !!orgId && !!projectId && !!sessionId,
    queryKey: ['apm-links', projectId, sessionId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${projectId}/integrations/apm-links/${sessionId}`))
        .data.data as ApmSessionLinks[],
  });
  const apmGroups = apm.data ?? [];
  const apmLinkCount = apmGroups.reduce((n, g) => n + g.links.length, 0);

  // Which backend code this session's actions ran, from a coverage tool that
  // shares our keys — the SDK's W3C trace id per request, and the session id it
  // puts in baggage. Fetched through our own API: the credential reads
  // source-level coverage and never belongs in a page.
  const backend = useQuery({
    enabled: !!orgId && !!projectId && !!sessionId,
    queryKey: ['backend-session', projectId, sessionId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${projectId}/integrations/backend/${sessionId}`))
        .data.data as BackendSessionResult | null,
    // A session's backend work does not change once recorded, and the far side
    // is somebody else's rate limit.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const backendResult = backend.data ?? null;
  const backendRequests = backendResult?.session?.requests ?? [];

  /**
   * Which step of the replay each backend request belongs to.
   *
   * The two products share a session id, not a clock: the browser timestamps
   * the recording and the agents timestamp the server work, and the two
   * machines disagree by however much they disagree. So a request is filed
   * under the last marker at or before it, with the same 200ms of slack the
   * scrubber already uses to decide which step is current — and anything that
   * lands before the first marker is shown apart rather than pushed onto the
   * nearest one. A mis-filed call chain is worse than an unfiled one: it tells
   * somebody a click ran code it did not.
   */
  const backendByStep = useMemo(() => {
    if (!timeline || backendRequests.length === 0) {
      return { steps: [] as { marker: TimelineMarker; requests: BackendRequest[] }[], loose: backendRequests };
    }
    const markers = timeline.markers;
    const buckets = markers.map((marker) => ({ marker, requests: [] as BackendRequest[] }));
    const loose: BackendRequest[] = [];
    for (const r of backendRequests) {
      const offsetMs = r.startedAtMs - timeline.startTime;
      let idx = -1;
      for (let i = 0; i < markers.length; i++) {
        if (markers[i]!.offsetMs <= offsetMs + 200) idx = i;
        else break;
      }
      if (idx < 0) loose.push(r);
      else buckets[idx]!.requests.push(r);
    }
    return { steps: buckets.filter((b) => b.requests.length > 0), loose };
  }, [timeline, backendRequests]);


  // Fit the replay stage to the available width and a max height, preserving the
  // recorded viewport's aspect ratio (so mobile isn't stretched into a desktop box).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(el.clientWidth / stageDims.w, MAX_STAGE_H / stageDims.h));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageDims]);

  useEffect(() => {
    if (!data?.events || !stageRef.current) return;
    const events = data.events;
    if (events.length < 2) return;

    // Size the stage to the recorded viewport (first Meta event).
    const meta = events.find((e: { type: number }) => e.type === EventType.Meta) as
      | { data?: { width?: number; height?: number } }
      | undefined;
    if (meta?.data?.width && meta.data.height) {
      setStageDims({ w: meta.data.width, h: meta.data.height });
    }

    setInspectors(extractInspectors(events));
    stageRef.current.innerHTML = '';
    // Decided from the events, not the session's platform column, so a
    // recording made before that column existed still picks the right player.
    const native = isNativeRecording(events);
    const engine = native
      ? new NativeReplayEngine(events, stageRef.current)
      : new ReplayEngine(events, stageRef.current);
    if (engine instanceof NativeReplayEngine) {
      // Frames carry their own dimensions; a native app has no viewport Meta.
      const size = engine.getStageSize();
      setStageDims({ w: size.width, h: size.height });
    }
    engineRef.current = engine;
    setTimeline(engine.getTimeline());
    const off = engine.on(setState);
    // Auto-play so the reconstructed page renders immediately instead of
    // freezing on the initial (often near-empty) keyframe.
    engine.play();

    return () => {
      off();
      engine.destroy();
      engineRef.current = null;
    };
  }, [data]);

  const engine = () => engineRef.current;
  const jump = (offsetMs: number) => engine()?.seek(offsetMs);

  /** Resolve a minified error stack to original source via uploaded sourcemaps. */
  const resolveStack = async (i: number, stack: string) => {
    setResolving(i);
    try {
      const res = await api.post(`/v1/orgs/${orgId}/projects/${projectId}/sourcemaps/symbolicate`, {
        releaseVersion: session?.releaseVersion ?? '',
        stack,
      });
      setResolvedStacks((r) => ({ ...r, [i]: res.data.data as StackFrame[] }));
    } catch {
      /* no maps / failed — leave the raw stack */
    } finally {
      setResolving(null);
    }
  };

  // The timeline marker the playhead has most recently reached — used to
  // highlight the current step and follow playback in the Timeline tab. Markers
  // are sorted by offset, so the last one at/under the playhead wins.
  const activeMarker = useMemo(() => {
    const markers = timeline?.markers ?? [];
    let idx = -1;
    for (let i = 0; i < markers.length; i++) {
      if (markers[i]!.offsetMs <= state.currentTimeMs + 200) idx = i;
      else break;
    }
    return idx;
  }, [timeline, state.currentTimeMs]);

  // Follow the video: scroll the active marker into view while playing, but
  // only nudge the tab's own scroll container — never the page — and only when
  // the item is actually out of view, so it doesn't fight a paused reviewer.
  const activeItemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tab !== 0 || !state.playing) return;
    const el = activeItemRef.current;
    const scroller = el?.closest('.replay-inspector-scroll') as HTMLElement | null;
    if (!el || !scroller) return;
    const er = el.getBoundingClientRect();
    const sr = scroller.getBoundingClientRect();
    if (er.top < sr.top + 48 || er.bottom > sr.bottom - 8) {
      scroller.scrollTop += er.top - sr.top - 56;
    }
  }, [activeMarker, tab, state.playing]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography variant="h6">Session Replay</Typography>
        <Typography variant="body2" color="text.secondary">
          {sessionId}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {steps.length > 0 && (
          <Tooltip title={t.rerunHint}>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={() => setRerunOpen(true)}
              disabled={!orgId || !projectId || !sessionId}
            >
              Re-run
            </Button>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          startIcon={<ShareIcon />}
          onClick={() => setShareOpen(true)}
          disabled={!orgId || !projectId || !sessionId}
        >
          Share
        </Button>
        {canTest && (
          <Tooltip title={t.annotateHint}>
            <span>
              <Button
                variant="contained"
                startIcon={<BugReportIcon />}
                onClick={openTesterMode}
                disabled={!orgId || !projectId || !sessionId}
              >
                Tester mode
              </Button>
            </span>
          </Tooltip>
        )}
        {canTest && (
          <Button
            variant="outlined"
            startIcon={<ConfirmationNumberIcon />}
            onClick={() => setIssueOpen(true)}
            disabled={!orgId || !projectId || !sessionId}
          >
            Create issue
          </Button>
        )}
      </Stack>

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        orgId={orgId}
        projectId={projectId}
        sessionId={sessionId}
      />

      <RerunDialog
        open={rerunOpen}
        onClose={() => setRerunOpen(false)}
        orgId={orgId}
        projectId={projectId}
        sessionId={sessionId}
        entryUrl={session?.entryUrl}
        stepsCount={steps.length}
      />

      <TesterModeDialog
        open={testerOpen}
        onClose={() => setTesterOpen(false)}
        background={testerBg}
        context={testerContext}
        defaultTitle={sessionId ? issueTitle(sessionId, inspectors) : t.bugReport}
        submitting={testerTicket.isPending}
        error={testerErr}
        onSubmit={(input) => testerTicket.mutate(input)}
      />

      {orgId && projectId && sessionId && (
        <CreateIssueDialog
          open={issueOpen}
          onClose={() => setIssueOpen(false)}
          orgId={orgId}
          projectId={projectId}
          sessionId={sessionId}
          defaultTitle={issueTitle(sessionId, inspectors)}
          description={issueBody({ reportedFrom: t.reportedFrom }, sessionId, timeline, inspectors)}
          jsErrors={inspectors?.errors.length}
        />
      )}

      <VisitorDetails session={session} actionCount={session?.interactionCount} />

      {/* DevTools layout: the player with the AI summary at its side on top,
          the full-width inspector docked below. The panel gets the whole row
          for the waterfall and payloads. */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Box sx={{ flex: '1 1 640px', minWidth: 0 }}>
          <Paper sx={{ p: 0, overflow: 'hidden' }}>
            {isLoading && <Typography sx={{ p: 2 }}>{t.loading}</Typography>}
            {missingSnapshot && (
              <Alert severity="warning" sx={{ m: 2 }}>
                This recording has no DOM snapshot, so there is no video to play, the session's
                first payload never reached the collector (typically it exceeded the ingest body
                limit). Console, Network, Errors and Steps below are still complete.
              </Alert>
            )}
            <Box
              ref={containerRef}
              sx={{ width: '100%', height: stageDims.h * scale, bgcolor: '#fff', display: 'flex', justifyContent: 'center' }}
            >
              {/* Scaled footprint, centred; the stage inside is the recorded viewport. */}
              <Box sx={{ position: 'relative', width: stageDims.w * scale, height: stageDims.h * scale, overflow: 'hidden' }}>
                <Box
                  ref={stageRef}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: stageDims.w,
                    height: stageDims.h,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    '& .replayer-wrapper': { position: 'absolute', top: 0, left: 0, transform: 'none' },
                    '& iframe': { border: 'none' },
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Video-like controls */}
          <Paper sx={{ p: 1, mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => engine()?.toggle()} color="primary">
                {state.playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 90 }}>
                {fmt(state.currentTimeMs)} / {fmt(state.durationMs)}
              </Typography>
              <ReplayScrubber
                durationMs={state.durationMs}
                currentTimeMs={state.currentTimeMs}
                markers={timeline?.markers ?? []}
                onSeek={jump}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={state.speed}
                onChange={(_, v) => v && engine()?.setSpeed(v as PlaybackSpeed)}
              >
                {[1, 2, 4, 8].map((s) => (
                  <ToggleButton key={s} value={s}>
                    {s}x
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
            {highlights.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, px: 1, pb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mr: 0.5 }}>
                  Highlights
                </Typography>
                {highlights.map((h) => (
                  <Chip
                    key={`${h.kind}-${h.offsetMs}`}
                    size="small"
                    clickable
                    onClick={() => jump(h.offsetMs)}
                    icon={<span style={{ fontSize: 12, lineHeight: 1, paddingLeft: 6 }}>{HIGHLIGHT_GLYPH[h.kind]}</span> as never}
                    label={`${fmt(h.offsetMs)} · ${h.label}`}
                    sx={{
                      bgcolor: `${HIGHLIGHT_COLOR[h.kind]}18`,
                      color: HIGHLIGHT_COLOR[h.kind],
                      fontWeight: 600,
                      maxWidth: 340,
                    }}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        </Box>

        {/* AI summary at the player's side. Generation streams in as it writes. */}
        <SessionSummary
          orgId={orgId}
          projectId={projectId}
          sessionId={sessionId}
          moments={(timeline?.markers ?? []).filter((m) => m.type === 'rageClick' || m.type === 'error')}
          onJump={(ms) => {
            jump(ms);
            containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
        </Box>

        <Paper className="replay-inspector-scroll" sx={{ width: '100%', maxHeight: 460, overflow: 'auto' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={t.tabs.timeline} />
            <Tab label={tabLabel(t.tabs.console, logEntries.length || undefined)} />
            <Tab label={tabLabel(t.tabs.network, inspectors?.network.length)} />
            <Tab label={tabLabel(t.tabs.errors, inspectors?.errors.length)} />
            <Tab label={t.tabs.vitals} />
            <Tab label={tabLabel(t.tabs.correlate, apmLinkCount)} />
            <Tab label={t.tabs.dom} />
            <Tab label={tabLabel(t.tabs.perf, inspectors?.perf.length || undefined)} />
            <Tab label={tabLabel(t.tabs.steps, steps.length || undefined)} />
            <Tab label={tabLabel(t.tabs.backend, backendResult?.session?.methodCount || undefined)} />
          </Tabs>

          {tab === 0 && timeline && (
            <List dense>
              {timeline.markers.map((m, i) => {
                const active = i === activeMarker;
                const future = i > activeMarker;
                return (
                  <ListItemButton
                    key={i}
                    ref={active ? activeItemRef : undefined}
                    selected={active}
                    onClick={() => jump(m.offsetMs)}
                    // Dynamic styles go through inline `style` (not `sx`): under the
                    // rapid re-renders while playing, MUI's Emotion-cached `sx`
                    // classes can stick to a stale value, whereas React writes
                    // `style` to the node on every render.
                    style={{
                      opacity: future ? 0.5 : 1,
                      borderLeft: `3px solid ${active ? MARKER_COLOR.click : 'transparent'}`,
                      transition: 'opacity .2s ease',
                    }}
                  >
                    <Chip
                      label={m.type}
                      size="small"
                      sx={{ bgcolor: MARKER_COLOR[m.type], mr: 1, color: '#fff' }}
                    />
                    <ListItemText primary={m.label} secondary={`${(m.offsetMs / 1000).toFixed(1)}s`} />
                  </ListItemButton>
                );
              })}
            </List>
          )}

          {tab === 1 && inspectors && (
            <Box>
              <Stack direction="row" spacing={0.5} sx={{ px: 1.5, py: 1, flexWrap: 'wrap', gap: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                {LOG_LEVELS.filter((l) => l.key === 'all' || (logCounts[l.key] ?? 0) > 0).map((l) => (
                  <Chip
                    key={l.key}
                    size="small"
                    clickable
                    label={`${t.logLevels[l.key] ?? l.key}${logCounts[l.key] ? ` ${logCounts[l.key]}` : ''}`}
                    variant={logLevel === l.key ? 'filled' : 'outlined'}
                    color={logLevel === l.key ? 'primary' : 'default'}
                    onClick={() => setLogLevel(l.key)}
                    sx={{ height: 22, fontSize: 11 }}
                  />
                ))}
              </Stack>
              {shownLogs.length === 0 && <Empty>{t.noConsole}</Empty>}
              {(logShowAll ? shownLogs : shownLogs.slice(0, ROW_CAP)).map((c, i) => (
                <Box
                  key={i}
                  onClick={() => jump(c.offsetMs)}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    px: 1.5,
                    py: 0.75,
                    cursor: 'pointer',
                    borderLeft: `3px solid ${LEVEL_COLOR[c.level] ?? '#6b7280'}`,
                    bgcolor: c.level === 'error' ? 'rgba(239,68,68,.08)' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Chip
                    label={c.level}
                    size="small"
                    sx={{ bgcolor: LEVEL_COLOR[c.level] ?? '#6b7280', color: '#fff', height: 18, fontSize: 10 }}
                  />
                  <Box
                    component="code"
                    sx={{ flex: 1, fontSize: 12, wordBreak: 'break-word', color: 'text.primary' }}
                  >
                    {c.message}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {secs(c.offsetMs)}
                  </Typography>
                </Box>
              ))}
              {!logShowAll && shownLogs.length > ROW_CAP && (
                <Button fullWidth size="small" onClick={() => setLogShowAll(true)} sx={{ my: 1 }}>
                  Show all {shownLogs.length} rows
                </Button>
              )}
            </Box>
          )}

          {tab === 2 && inspectors && (
            <Box>
              <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  {NET_TYPES.filter((nt) => nt.key === 'all' || nt.key === 'xhr' || (netCounts[nt.key] ?? 0) > 0).map((nt) => (
                    <Chip
                      key={nt.key}
                      size="small"
                      clickable
                      label={`${t.netTypes[nt.key] ?? nt.key}${netCounts[nt.key] ? ` ${netCounts[nt.key]}` : ''}`}
                      variant={netType === nt.key ? 'filled' : 'outlined'}
                      color={netType === nt.key ? 'primary' : 'default'}
                      onClick={() => { setNetType(nt.key); setNetOpen(null); }}
                      sx={{ height: 22, fontSize: 11 }}
                    />
                  ))}
                  <TextField
                    size="small"
                    value={netQuery}
                    onChange={(e) => { setNetQuery(e.target.value); setNetOpen(null); }}
                    placeholder={t.filterNetwork}
                    sx={{ ml: 'auto', width: 180, '& .MuiInputBase-input': { py: 0.4, fontSize: 12 } }}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" sx={{ mt: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {netEntries.length} requests ·{' '}
                    {netEntries.filter((n) => !n.ok).length} failed ·{' '}
                    {netEntries.filter((n) => n.slow).length} slow
                  </Typography>
                  {/* Time ruler over the waterfall column. */}
                  <Stack direction="row" justifyContent="space-between" sx={{ width: WF_W, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>0:00</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>{fmt(wfTotal / 2)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>{fmt(wfTotal)}</Typography>
                  </Stack>
                </Stack>
              </Box>
              {netEntries.length === 0 && <Empty>{t.noNetwork}</Empty>}
              <Box sx={{ position: 'relative' }}>
              {/* Playhead: where playback currently is, on the same axis as the bars. */}
              {netEntries.length > 0 && (
                <Box
                  style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: WF_PAD + (1 - Math.min(1, state.currentTimeMs / wfTotal)) * WF_W,
                    width: 0,
                    borderLeft: `2px solid ${PLAYHEAD}`,
                    opacity: 0.7,
                    pointerEvents: 'none',
                    zIndex: 2,
                  }}
                />
              )}
              {(netShowAll ? netEntries : netEntries.slice(0, ROW_CAP)).map((n, i) => {
                const open = netOpen === i;
                // Every request expands: the General facts are always worth a
                // look; payloads/headers appear when body capture recorded them.
                const hasDetail = true;
                return (
                  <Box key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      onClick={() => setNetOpen(open ? null : i)}
                      sx={{ px: 1.5, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      {hasDetail ? (
                        <ChevronRightIcon
                          sx={{ fontSize: 16, color: 'text.secondary', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}
                        />
                      ) : (
                        <Box sx={{ width: 16 }} />
                      )}
                      <Chip
                        label={n.method}
                        size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, minWidth: 46, bgcolor: 'action.selected', color: methodColor(n.method), fontFamily: 'ui-monospace, monospace', '& .MuiChip-label': { px: 0.75 } }}
                      />
                      <Chip
                        label={n.status || (n.kind === 'resource' ? '—' : 'ERR')}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          // A static resource with no readable status is unknown, not failed.
                          bgcolor: n.kind === 'resource' && !n.status ? 'action.selected' : statusColor(n.status),
                          color: n.kind === 'resource' && !n.status ? 'text.secondary' : '#fff',
                          minWidth: 40,
                        }}
                      />
                      {n.operationName && (
                        <Chip
                          label={`gql: ${n.operationName}`}
                          size="small"
                          sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#e0335522', color: '#e11d48', fontFamily: 'ui-monospace, monospace', '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                      <Box sx={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.primary' }}>
                        {shortUrl(n.url)}
                      </Box>
                      <Typography variant="caption" sx={{ color: n.slow ? '#f59e0b' : 'text.secondary', fontWeight: n.slow ? 700 : 400, whiteSpace: 'nowrap' }}>
                        {n.durationMs}ms{n.sizeBytes ? ` · ${(n.sizeBytes / 1024).toFixed(1)} kB` : ''}
                      </Typography>
                      <Tooltip title={t.jumpToRequest}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); jump(n.startMs); }}>
                          <PlayArrowIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      {/* Waterfall bar: [start, completion] on the session axis. */}
                      <Box sx={{ width: WF_W, flexShrink: 0, position: 'relative', height: 14, borderRadius: 0.5, bgcolor: 'action.hover', overflow: 'hidden' }}>
                        <Box
                          style={{
                            position: 'absolute',
                            top: 3,
                            bottom: 3,
                            left: `${(n.startMs / wfTotal) * 100}%`,
                            width: `${Math.max(1.5, ((n.offsetMs - n.startMs) / wfTotal) * 100)}%`,
                            borderRadius: 2,
                            background: !n.ok ? '#ef4444' : n.slow ? '#f59e0b' : TEAL,
                          }}
                        />
                      </Box>
                    </Stack>
                    {n.error && (
                      <Typography variant="caption" sx={{ color: '#ef4444', pl: 6, pb: 0.5, display: 'block' }}>
                        {n.error}
                      </Typography>
                    )}
                    {open && hasDetail && (
                      <Box sx={{ px: 2, pb: 1.5, bgcolor: 'action.hover' }}>
                        <PayloadSection title={t.general}>
                          <KeyVals
                            rows={{
                              URL: n.url,
                              Status: n.status ? String(n.status) : n.kind === 'resource' ? 'unknown (opaque)' : `failed${n.error ? `, ${n.error}` : ''}`,
                              Type: n.resourceType.toUpperCase() + (n.kind === 'resource' ? ' · static resource' : ` · ${n.kind}`),
                              Started: `${(n.startMs / 1000).toFixed(2)}s`,
                              Duration: `${n.durationMs}ms`,
                              ...(n.sizeBytes ? { Size: `${(n.sizeBytes / 1024).toFixed(1)} kB` } : {}),
                              ...(n.operationType ? { [t.graphql]: `${n.operationType}${n.operationName ? ' · ' + n.operationName : ''}` } : {}),
                              ...(n.traceId ? { [t.traceId]: n.traceId } : {}),
                            }}
                          />
                        </PayloadSection>
                        {n.reqHeaders && <PayloadSection title={t.reqHeaders}><KeyVals rows={n.reqHeaders} mono /></PayloadSection>}
                        {n.reqBody && <PayloadSection title={t.reqPayload}><BodyBlock text={n.reqBody} /></PayloadSection>}
                        {n.resHeaders && <PayloadSection title={t.resHeaders}><KeyVals rows={n.resHeaders} mono /></PayloadSection>}
                        {n.resBody && <PayloadSection title={t.resPayload}><BodyBlock text={n.resBody} /></PayloadSection>}
                      </Box>
                    )}
                  </Box>
                );
              })}
              {!netShowAll && netEntries.length > ROW_CAP && (
                <Button fullWidth size="small" onClick={() => setNetShowAll(true)} sx={{ my: 1 }}>
                  Show all {netEntries.length} requests
                </Button>
              )}
              </Box>
            </Box>
          )}

          {tab === 3 && inspectors && (
            <Box sx={{ p: 1 }}>
              {inspectors.errors.length === 0 && <Empty>{t.noErrors}</Empty>}
              {inspectors.errors.map((e, i) => (
                <Box
                  key={i}
                  onClick={() => jump(e.offsetMs)}
                  sx={{ p: 1.5, mb: 1, cursor: 'pointer', borderRadius: 1, bgcolor: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip label={e.kind} size="small" color="error" sx={{ height: 18, fontSize: 10 }} />
                    <Typography variant="caption" color="text.secondary">
                      {secs(e.offsetMs)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {e.message}
                  </Typography>
                  {e.source && (
                    <Typography variant="caption" color="text.secondary">
                      {shortUrl(e.source)}
                      {e.line ? `:${e.line}` : ''}
                    </Typography>
                  )}
                  {e.stack && !resolvedStacks[i] && (
                    <>
                      <Button
                        size="small"
                        variant="text"
                        disabled={resolving === i}
                        onClick={(ev) => { ev.stopPropagation(); resolveStack(i, e.stack!); }}
                        sx={{ mt: 0.5, px: 0.5, minWidth: 0, textTransform: 'none' }}
                      >
                        {resolving === i ? t.resolving : t.resolve}
                      </Button>
                      <Box component="pre" sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>
                        {e.stack}
                      </Box>
                    </>
                  )}
                  {resolvedStacks[i] && (
                    <Box sx={{ mt: 0.75 }}>
                      {resolvedStacks[i]!.map((f, j) => (
                        <Box key={j} sx={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: f.resolved ? 'text.primary' : 'text.disabled', py: 0.1 }}>
                          {f.resolved ? (
                            <>
                              <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>{f.resolved.name || f.function || '(anonymous)'}</Box>
                              {'  '}
                              <Box component="span" sx={{ color: 'text.secondary' }}>{f.resolved.source}:{f.resolved.line}</Box>
                            </>
                          ) : (
                            f.raw
                          )}
                        </Box>
                      ))}
                      {resolvedStacks[i]!.every((f) => !f.resolved) && (
                        <Typography variant="caption" color="text.secondary">
                          No matching sourcemap for release <b>{session?.releaseVersion || '—'}</b>. Upload it under Sourcemaps.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {tab === 4 && inspectors && (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Core Web Vitals
              </Typography>
              {inspectors.vitals.length === 0 && <Empty>{t.noVitals}</Empty>}
              <Stack spacing={1}>
                {inspectors.vitals.map((v, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ width: 48, fontWeight: 700 }}>{v.metric}</Typography>
                    <Typography sx={{ flex: 1 }}>
                      {v.metric === 'CLS' ? v.value.toFixed(3) : `${Math.round(v.value)}ms`}
                    </Typography>
                    <Chip label={v.rating} size="small" sx={{ bgcolor: VITAL_COLOR[v.rating], color: '#fff' }} />
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {tab === 5 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Correlate this session
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Jump from this replay to the same visitor in your APM (via W3C trace context) or
                Google Analytics (via GA client id).
              </Typography>
              {apm.isLoading && <Empty>{t.loadingLinks}</Empty>}
              {!apm.isLoading && apmGroups.length === 0 && (
                <Empty>
                  No correlation integration connected. Add an APM (New Relic, Datadog,
                  AppDynamics, OpenTelemetry) or Google Analytics in Integrations to jump from a
                  session to its trace or GA visitor.
                </Empty>
              )}
              <Stack spacing={2} sx={{ mt: 1.5 }}>
                {apmGroups.map((g) => (
                  <Box key={g.provider}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {g.provider}
                    </Typography>
                    <Stack spacing={0.5}>
                      {g.links.map((l, i) => (
                        <Button
                          key={i}
                          size="small"
                          variant={l.kind === 'search' || l.kind === 'replay' ? 'contained' : 'outlined'}
                          endIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                          href={safeHttpUrl(l.url) || '#'}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ justifyContent: 'space-between', textTransform: 'none', fontFamily: l.traceId ? 'monospace' : undefined }}
                        >
                          {l.label}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {tab === 6 && <DomInspector events={data?.events ?? []} />}

          {tab === 7 && inspectors && <PerfPanel perf={inspectors.perf} onJump={jump} />}

          {tab === 8 && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  onClick={() =>
                    setDoneSteps(doneSteps.size === steps.length ? new Set() : new Set(steps.map((_, i) => i)))
                  }
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <input
                    type="checkbox"
                    checked={steps.length > 0 && doneSteps.size === steps.length}
                    readOnly
                    ref={(el) => {
                      // Indeterminate when some (not all) steps are ticked.
                      if (el) el.indeterminate = doneSteps.size > 0 && doneSteps.size < steps.length;
                    }}
                    style={{ accentColor: TEAL, cursor: 'pointer' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {doneSteps.size === steps.length && steps.length > 0 ? t.clearAll : t.selectAll}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {doneSteps.size}/{steps.length} done
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                  onClick={async () => {
                    await navigator.clipboard.writeText(
                      steps.map((s, i) => `${i + 1}. [${fmt(s.offsetMs)}] ${s.label}`).join('\n'),
                    );
                    setStepsCopied(true);
                    setTimeout(() => setStepsCopied(false), 1500);
                  }}
                >
                  {stepsCopied ? t.copied : t.copySteps}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setRerunOpen(true)}
                  disabled={!orgId || !projectId || !sessionId}
                >
                  Re-run
                </Button>
              </Stack>
              {steps.length === 0 && <Empty>{t.noSteps}</Empty>}
              {steps.map((s, i) => {
                const done = doneSteps.has(i);
                return (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      opacity: done ? 0.45 : 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={done}
                      onChange={() =>
                        setDoneSteps((prev) => {
                          const next = new Set(prev);
                          done ? next.delete(i) : next.add(i);
                          return next;
                        })
                      }
                      style={{ accentColor: TEAL, cursor: 'pointer' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 20, textAlign: 'right' }}>
                      {i + 1}.
                    </Typography>
                    <Chip
                      label={s.kind}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        minWidth: 64,
                        color: '#fff',
                        bgcolor: s.kind === 'click' ? TEAL : s.kind === 'input' ? '#3b82f6' : '#22c55e',
                      }}
                    />
                    <Box
                      onClick={() => jump(s.offsetMs)}
                      sx={{ flex: 1, fontSize: 13, cursor: 'pointer', textDecoration: done ? 'line-through' : 'none' }}
                    >
                      {s.label}
                      {s.selectors?.[0] && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontFamily: 'ui-monospace, monospace' }}>
                          {s.selectors[0]}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(s.offsetMs)}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
          )}

          {tab === 9 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                What the backend ran
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Each request this session made, and the methods it executed on the far side.
                Matched by the trace id the SDK stamps on every call, so a click that fired
                three requests at once is three rows, not a guess.
              </Typography>

              {backend.isLoading && <Empty>{t.askingCoverage}</Empty>}

              {backend.isError && (
                <Empty>
                  Could not reach the coverage tool. Check the CoverOps integration under
                  Integrations. Its link token may have been revoked.
                </Empty>
              )}

              {!backend.isLoading && !backend.isError && !backendResult && (
                <Empty>
                  No coverage tool connected. Add CoverOps under Integrations to see which of
                  your own methods each action in this session ran.
                </Empty>
              )}

              {backendResult?.notIncluded && <Empty>{t.coverOpsNotIncluded}</Empty>}

              {backendResult && !backendResult.notIncluded && backendRequests.length === 0 && (
                <Empty>
                  {backendResult.provider} recorded nothing for this session. The usual cause is
                  tracing being off in the instrumented service. The numbers still work, but
                  call chains need it switched on.
                </Empty>
              )}

              {backendRequests.length > 0 && (
                <Stack spacing={2} sx={{ mt: 1.5 }}>
                  {backendByStep.steps.map(({ marker, requests }) => (
                    <Box key={`${marker.offsetMs}-${marker.label}`}>
                      {/* The step is a heading, not a row: everything under it
                          is what that one action set off on the server. */}
                      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => jump(marker.offsetMs)}
                          sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                        >
                          {fmt(marker.offsetMs)}
                        </Link>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {marker.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {requests.length} request{requests.length === 1 ? '' : 's'}
                        </Typography>
                      </Stack>
                      <Stack spacing={1} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                        {requests.map((r) => (
                          <BackendRequestCard
                            key={r.traceId}
                            request={r}
                            orgId={orgId}
                            projectId={projectId}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ))}

                  {backendByStep.loose.length > 0 && (
                    <Box>
                      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Before the first step
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {/* Said rather than guessed at. The two clocks are
                              different machines', and work that lands outside
                              the recording is filed nowhere instead of onto
                              whichever step happens to be nearest. */}
                          work the recording does not cover, or two clocks apart
                        </Typography>
                      </Stack>
                      <Stack spacing={1} sx={{ pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                        {backendByStep.loose.map((r) => (
                          <BackendRequestCard
                            key={r.traceId}
                            request={r}
                            orgId={orgId}
                            projectId={projectId}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          )}
        </Paper>
      </Box>

    </Box>
  );
}

/**
 * One server request of a step, with its call chain — and, on request, the
 * lines each method is on.
 *
 * The snippet is fetched per method rather than up front. A step can run
 * forty methods across three services and nobody reads forty snippets; the
 * one that gets opened is the one somebody is actually asking about.
 */
function BackendRequestCard({
  request,
  orgId,
  projectId,
}: {
  request: BackendRequest;
  orgId?: string;
  projectId?: string;
}) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {request.label || request.service}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {request.service}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {request.durationMs}ms · {request.methods.length} methods
        </Typography>
      </Stack>
      <Stack spacing={0.25} sx={{ mt: 0.75 }}>
        {request.methods.map((m, i) => {
          const line = m.lines.length > 0 ? m.lines[0] : undefined;
          const canOpen = !!m.filePath;
          return (
            <Box key={`${request.traceId}:${i}`}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="baseline"
                sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
              >
                {/* Depth is what makes a call chain readable as one. */}
                <Box sx={{ pl: `${Math.min(m.depth, 8) * 12}px`, flex: 1 }}>
                  {m.className ? `${m.className}.` : ''}
                  {m.method}
                </Box>
                {canOpen && (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => setOpen(open === i ? null : i)}
                    sx={{ fontFamily: 'inherit', fontSize: 11 }}
                  >
                    {m.filePath}
                    {line ? `:${line}` : ''}
                  </Link>
                )}
              </Stack>
              {open === i && m.filePath && (
                <SourceSnippet
                  orgId={orgId}
                  projectId={projectId}
                  filePath={m.filePath}
                  line={line}
                  ranLines={m.lines}
                />
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

/** The lines themselves, with the ones this method ran marked. */
function SourceSnippet({
  orgId,
  projectId,
  filePath,
  line,
  ranLines,
}: {
  orgId?: string;
  projectId?: string;
  filePath: string;
  line?: number;
  ranLines: number[];
}) {
  const q = useQuery({
    enabled: !!orgId && !!projectId,
    queryKey: ['backend-source', projectId, filePath, line ?? 0],
    queryFn: async () =>
      (
        await api.get(
          `/v1/orgs/${orgId}/projects/${projectId}/integrations/backend-source`,
          { params: { file: filePath, line } },
        )
      ).data.data as {
        filePath: string;
        commitSha: string | null;
        fromLine: number;
        focusLine: number | null;
        lines: string[];
      } | null,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  if (q.isLoading) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
        Fetching the lines…
      </Typography>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
        {/* The ordinary case, not a fault: a project can run coverage without
            ever letting its source leave its own machines. */}
        No source stored for this file in the coverage tool.
      </Typography>
    );
  }

  const ran = new Set(ranLines);
  return (
    <Box
      sx={{
        mt: 0.5,
        mb: 1,
        ml: 2,
        borderRadius: 1,
        bgcolor: 'action.hover',
        overflow: 'auto',
        fontFamily: 'ui-monospace, monospace',
        fontSize: 11.5,
      }}
    >
      {q.data.lines.map((text, i) => {
        const n = q.data!.fromLine + i;
        return (
          <Box
            key={n}
            sx={{
              display: 'flex',
              gap: 1.5,
              px: 1,
              // The lines this method actually executed, against the ones that
              // are merely nearby — the difference is the entire point of
              // showing a window rather than a single line.
              bgcolor: ran.has(n) ? 'success.main' : 'transparent',
              color: ran.has(n) ? 'success.contrastText' : 'inherit',
            }}
          >
            <Box sx={{ opacity: 0.6, minWidth: 34, textAlign: 'right', userSelect: 'none' }}>{n}</Box>
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre' }}>
              {text}
            </Box>
          </Box>
        );
      })}
      {q.data.commitSha && (
        <Box sx={{ px: 1, py: 0.5, opacity: 0.6 }}>{q.data.commitSha.slice(0, 8)}</Box>
      )}
    </Box>
  );
}

/** Inline AI summary for the session — shows the stored one, or generates on demand. */
function SessionSummary({
  orgId,
  projectId,
  sessionId,
  moments,
  onJump,
}: {
  orgId?: string;
  projectId?: string;
  sessionId?: string;
  moments: Array<{ offsetMs: number; type: string; label: string }>;
  onJump: (ms: number) => void;
}) {
  const t = useT().replay.page;
  const qc = useQueryClient();
  const enabled = !!orgId && !!projectId && !!sessionId;
  const base = `/v1/orgs/${orgId}/projects/${projectId}/sessions/${sessionId}/summary`;

  interface Sum { summary: string | null; model?: string; source?: 'ai' | 'heuristic' }
  const cached = useQuery({
    enabled,
    queryKey: ['session-summary', sessionId],
    queryFn: async () => (await api.get(base)).data as Sum,
  });
  const gen = useMutation({
    mutationFn: async (force: boolean) => (await api.post(base, { force })).data as Sum,
    onSuccess: (d) => qc.setQueryData(['session-summary', sessionId], d),
  });

  // Freshly generated summaries stream in like they're being written; a stored
  // one renders instantly. `typed` is the visible prefix while animating.
  const [typed, setTyped] = useState<string | null>(null);
  useEffect(() => {
    const full = gen.data?.summary;
    if (!full) return;
    let i = 0;
    setTyped('');
    const id = setInterval(() => {
      i = Math.min(full.length, i + 3);
      setTyped(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(id);
        setTyped(null);
      }
    }, 16);
    return () => clearInterval(id);
  }, [gen.data]);

  const data = gen.data ?? cached.data;
  const hasSummary = !!data?.summary;

  return (
    <Paper sx={{ p: 2.5, flex: '1 1 300px', minWidth: 280, maxWidth: 420, maxHeight: 620, overflow: 'auto', alignSelf: 'stretch' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: hasSummary ? 1.5 : 0 }}>
        <AutoAwesomeIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700}>{t.aiSummary}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {hasSummary && (
          <Button
            size="small"
            startIcon={gen.isPending ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
            disabled={gen.isPending || !enabled}
            onClick={() => gen.mutate(true)}
          >
            {gen.isPending ? t.regenerating : t.regenerate}
          </Button>
        )}
      </Stack>

      {cached.isLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
      {gen.isError && <Alert severity="error" sx={{ mb: 1 }}>{t.summaryFailed}</Alert>}

      {hasSummary ? (
        <>
          <Markdown text={typed ?? data!.summary!} />
          <Chip
            size="small"
            sx={{ mt: 1.5 }}
            icon={<AutoAwesomeIcon sx={{ fontSize: 13 }} />}
            variant={data!.source === 'ai' ? 'filled' : 'outlined'}
            color={data!.source === 'ai' ? 'primary' : 'default'}
            label={data!.source === 'ai' ? (data!.model ?? t.ai) : t.heuristicNoKey}
          />
        </>
      ) : (
        !cached.isLoading && (
          <Stack alignItems="center" spacing={1.5} sx={{ mt: 4, mb: 2, textAlign: 'center', px: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {t.noSummaryYet}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              startIcon={gen.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              disabled={gen.isPending || !enabled}
              onClick={() => gen.mutate(false)}
              sx={{ maxWidth: 280, textTransform: 'none', whiteSpace: 'nowrap', fontWeight: 700 }}
            >
              {gen.isPending ? t.generating : t.generateSummary}
            </Button>
          </Stack>
        )
      )}

      {/* Clickable friction moments. Jump the replay to that instant. */}
      {moments.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 0.75 }}>
            Jump to friction moments
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {moments.slice(0, 12).map((m, i) => {
              const rage = m.type === 'rageClick';
              return (
                <Chip
                  key={i}
                  size="small"
                  clickable
                  onClick={() => onJump(m.offsetMs)}
                  icon={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                  label={`${m.label} · ${(m.offsetMs / 1000).toFixed(1)}s`}
                  sx={{
                    fontWeight: 600,
                    color: rage ? '#e11d48' : '#ef4444',
                    bgcolor: rage ? 'rgba(225,29,72,.10)' : 'rgba(239,68,68,.10)',
                    '& .MuiChip-icon': { color: 'inherit' },
                    '&:hover': { bgcolor: rage ? 'rgba(225,29,72,.18)' : 'rgba(239,68,68,.18)' },
                  }}
                />
              );
            })}
          </Stack>
        </>
      )}
    </Paper>
  );
}

/** A labelled block inside the expanded network-request detail. */
function PayloadSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

/** Key/value list (headers, general facts). */
function KeyVals({ rows, mono }: { rows: Record<string, string>; mono?: boolean }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.25 }}>
      {Object.entries(rows).map(([k, v]) => (
        <Fragment key={k}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: mono ? 'ui-monospace, monospace' : undefined, whiteSpace: 'nowrap' }}>
            {k}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.primary', fontFamily: mono ? 'ui-monospace, monospace' : undefined, wordBreak: 'break-all' }}>
            {v}
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}

/** Pretty-printed request/response body (JSON reformatted when possible). */
function BodyBlock({ text }: { text: string }) {
  let pretty = text;
  try {
    pretty = JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    /* not JSON — show raw */
  }
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        maxHeight: 240,
        overflow: 'auto',
        fontSize: 11,
        fontFamily: 'ui-monospace, monospace',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {pretty}
    </Box>
  );
}

/** Runtime performance panel: memory / DOM nodes / FPS summarised + over time. */
function PerfPanel({ perf, onJump }: { perf: PerfSample[]; onJump: (ms: number) => void }) {
  const t = useT().replay.page;
  if (perf.length === 0) {
    return <Empty>{t.noPerf}</Empty>;
  }
  const fps = perf.map((p) => p.fps).filter((v): v is number => typeof v === 'number');
  const dom = perf.map((p) => p.domNodes).filter((v): v is number => typeof v === 'number');
  const mem = perf.map((p) => p.memoryMB).filter((v): v is number => typeof v === 'number');
  const avg = (a: number[]) => (a.length ? Math.round(a.reduce((s, n) => s + n, 0) / a.length) : 0);

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1, mb: 1.5 }}>
        <PerfStat label={t.avgFps} value={fps.length ? String(avg(fps)) : '—'} tone={avg(fps) < 30 && fps.length ? '#f59e0b' : undefined} />
        <PerfStat label={t.minFps} value={fps.length ? String(Math.min(...fps)) : '—'} tone={fps.length && Math.min(...fps) < 20 ? '#ef4444' : undefined} />
        <PerfStat label={t.peakDom} value={dom.length ? Math.max(...dom).toLocaleString() : '—'} />
        <PerfStat label={t.peakMemory} value={mem.length ? `${Math.max(...mem)} MB` : '—'} />
      </Box>
      <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {perf.map((p, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={2}
            onClick={() => onJump(p.offsetMs)}
            sx={{ px: 1.5, py: 0.5, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, fontSize: 12 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ width: 48 }}>{secs(p.offsetMs)}</Typography>
            <Typography variant="caption" sx={{ width: 70 }}>{p.fps != null ? `${p.fps} fps` : '—'}</Typography>
            <Typography variant="caption" sx={{ width: 110 }}>{p.domNodes != null ? `${p.domNodes.toLocaleString()} nodes` : '—'}</Typography>
            <Typography variant="caption">{p.memoryMB != null ? `${p.memoryMB} MB` : '—'}</Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

function PerfStat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 750, fontSize: 18, color: tone ?? 'text.primary' }}>{value}</Typography>
    </Box>
  );
}
